import type { LucideIcon } from 'lucide-react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  defaultDropAnimation,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/stores/tenantStore';
import {
  fetchKitchenOrders,
  notifyKitchenOrder,
  updateKitchenOrderPause,
  updateKitchenOrderPriority,
  updateKitchenOrderStatus,
  type KitchenOrder,
  type KitchenOrderPriority,
  type KitchenOrderItem,
  type KitchenOrderItemStatus,
  type OrderStatus,
} from '@/lib/api/orders';
import {
  ChefHat,
  Clock,
  CheckCircle,
  UtensilsCrossed,
  ArrowRight,
  Loader2,
  Bell,
  Pause,
  Play,
  AlertTriangle,
  Timer,
  User,
  Wallet,
  Activity,
  Package,
  ChevronDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { cn, sanitizeDisplayText } from '@/lib/utils';
import { ORDER_IN_FLIGHT_STATUSES, ORDER_REVERSIBLE_STATUSES, ORDER_STATUS_SEQUENCE } from '@/types/orderStatus';
import { useLocaleFormatter } from '@/lib/i18n/useLocaleFormatter';
import { useRestaurantSettings } from '@/features/restaurant/hooks/useRestaurantSettings';

type KitchenColumnId = 'NEW_ORDER' | 'ACCEPTED' | 'IN_PROGRESS' | 'COMPLETED' | 'SERVED' | 'CANCELLED';

interface KitchenColumnConfig {
  id: KitchenColumnId;
  statuses: OrderStatus[];
  gradient: string;
  border: string;
  icon: LucideIcon;
}

type KitchenColumnWithCopy = KitchenColumnConfig & { title: string; description: string };

export interface DecoratedKitchenItem extends KitchenOrderItem {
  computed: {
    elapsedSeconds: number | null;
    remainingSeconds: number | null;
    progressPercent: number | null;
    overdue: boolean;
    isRunning: boolean;
    isPaused: boolean;
  };
  status: KitchenOrderItemStatus;
}

export interface DecoratedKitchenOrder extends KitchenOrder {
  items: DecoratedKitchenItem[];
  derived: {
    placedAt: Date | null;
    elapsedSeconds: number;
    prepSeconds: number | null;
    progressPercent: number | null;
    remainingSeconds: number | null;
    estimatedReadyAt: string | null;
    overdue: boolean;
  };
}

type DraggedOrderData = {
  order: DecoratedKitchenOrder;
  column: KitchenColumnWithCopy;
  columnId: KitchenColumnId;
};

const REVERSIBLE_STATUS_SET = new Set<OrderStatus>(ORDER_REVERSIBLE_STATUSES);

const canTransitionStatus = (current: OrderStatus, target: OrderStatus): boolean => {
  if (current === target) {
    return false;
  }
  const currentIndex = ORDER_STATUS_SEQUENCE.indexOf(current);
  const targetIndex = ORDER_STATUS_SEQUENCE.indexOf(target);
  if (currentIndex === -1 || targetIndex === -1) {
    return false;
  }
  if (targetIndex > currentIndex) {
    return true;
  }
  return REVERSIBLE_STATUS_SET.has(current) && REVERSIBLE_STATUS_SET.has(target);
};

const parseApiError = (error: unknown): { code?: string; reason?: string } | null => {
  if (!(error instanceof Error) || !error.message) {
    return null;
  }
  try {
    return JSON.parse(error.message) as { code?: string; reason?: string };
  } catch {
    return null;
  }
};

export interface KitchenOrderCardProps {
  order: DecoratedKitchenOrder;
  column: KitchenColumnWithCopy;
  advanceLabel?: string;
  onAdvance?: () => void;
  onPriorityChange: (priority: KitchenOrderPriority) => void;
  onTogglePause: () => void;
  onNotify: () => void;
  isAdvancing: boolean;
  isUpdatingPriority: boolean;
  isTogglingPause: boolean;
  isNotifying: boolean;
  formatRelativeTime: (iso?: string | null) => string;
  formatAbsoluteTime: (iso?: string | null) => string;
  formatDuration: (seconds: number) => string;
}

const formatStatusName = (status: OrderStatus): string =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatItemStatusName = (status: KitchenOrderItemStatus): string =>
  status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const ITEM_STATUS_CONFIG: Record<
  KitchenOrderItemStatus,
  { badge: string; dot: string; tone: 'default' | 'positive' | 'warning' | 'danger' }
> = {
  QUEUED: {
    badge: 'bg-muted text-muted-foreground border border-border/60',
    dot: 'bg-slate-400',
    tone: 'default',
  },
  FIRED: {
    badge: 'bg-sky-100 text-sky-800 border border-sky-200 dark:bg-sky-500/10 dark:text-sky-200',
    dot: 'bg-sky-500',
    tone: 'default',
  },
  PARTIAL_READY: {
    badge: 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-200',
    dot: 'bg-amber-400',
    tone: 'warning',
  },
  READY: {
    badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200',
    dot: 'bg-emerald-500',
    tone: 'positive',
  },
  RECALLED: {
    badge: 'bg-rose-100 text-rose-800 border border-rose-200 dark:bg-rose-500/10 dark:text-rose-200',
    dot: 'bg-rose-500',
    tone: 'danger',
  },
  CANCELLED: {
    badge: 'bg-slate-200 text-slate-700 border border-slate-300 dark:bg-slate-700/40 dark:text-slate-200',
    dot: 'bg-slate-500',
    tone: 'default',
  },
};

const formatCountdown = (target: number | null | undefined, now: number): string | null => {
  if (!target || !Number.isFinite(target)) {
    return null;
  }
  const delta = target - now;
  if (delta <= 0) {
    return null;
  }
  const totalSeconds = Math.round(delta / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  if (totalHours < 48) {
    return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }
  const totalDays = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  return remainingHours > 0 ? `${totalDays}d ${remainingHours}h` : `${totalDays}d`;
};

const PRIORITY_CONFIG: Record<KitchenOrderPriority, { badge: string; dot: string; labelKey: string }> = {
  NORMAL: {
    badge: 'bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    labelKey: 'priority.normal',
  },
  HIGH: {
    badge: 'bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-500/10 dark:text-amber-300',
    dot: 'bg-amber-500',
    labelKey: 'priority.high',
  },
  URGENT: {
    badge: 'bg-destructive/10 text-destructive border border-destructive/20',
    dot: 'bg-destructive',
    labelKey: 'priority.urgent',
  },
};

const COLUMN_CONFIG: KitchenColumnConfig[] = [
  {
    id: 'NEW_ORDER',
    statuses: ['PENDING_PAYMENT', 'PAID'],
    gradient: 'bg-gradient-to-br from-amber-50 via-white to-white dark:from-amber-950/60 dark:via-slate-950 dark:to-slate-950',
    border: 'border-amber-100 dark:border-amber-900/40',
    icon: Wallet,
  },
  {
    id: 'ACCEPTED',
    statuses: ['ACCEPTED'],
    gradient: 'bg-gradient-to-br from-emerald-50 via-white to-white dark:from-emerald-950/50 dark:via-slate-950 dark:to-slate-950',
    border: 'border-emerald-100 dark:border-emerald-900/40',
    icon: Activity,
  },
  {
    id: 'IN_PROGRESS',
    statuses: ['IN_PROGRESS'],
    gradient: 'bg-gradient-to-br from-amber-50 via-white to-white dark:from-amber-950/50 dark:via-slate-950 dark:to-slate-950',
    border: 'border-amber-100 dark:border-amber-900/40',
    icon: ChefHat,
  },
  {
    id: 'COMPLETED',
    statuses: ['READY'],
    gradient: 'bg-gradient-to-br from-rose-50 via-white to-white dark:from-rose-950/50 dark:via-slate-950 dark:to-slate-950',
    border: 'border-rose-100 dark:border-rose-900/40',
    icon: CheckCircle,
  },
  {
    id: 'SERVED',
    statuses: ['SERVED', 'PICKED_UP', 'COMPLETED'],
    gradient: 'bg-gradient-to-br from-indigo-50 via-white to-white dark:from-indigo-950/50 dark:via-slate-950 dark:to-slate-950',
    border: 'border-indigo-100 dark:border-indigo-900/40',
    icon: UtensilsCrossed,
  },
  {
    id: 'CANCELLED',
    statuses: ['CANCELLED', 'VOIDED', 'REFUNDED'],
    gradient: 'bg-gradient-to-br from-red-50 via-white to-white dark:from-red-950/50 dark:via-slate-950 dark:to-slate-950',
    border: 'border-red-200 dark:border-red-900/40',
    icon: AlertTriangle,
  },
];
const COLUMN_DEFAULT_COPY: Record<KitchenColumnId, { title: string; description: string }> = {
  NEW_ORDER: { title: 'New orders', description: 'Waiting for acceptance' },
  ACCEPTED: { title: 'Accepted', description: 'Ready to start prep' },
  IN_PROGRESS: { title: 'In progress', description: 'Actively being prepared' },
  COMPLETED: { title: 'Completed', description: 'Ready to hand off' },
  SERVED: { title: 'Served', description: 'Already handed to the guest' },
  CANCELLED: { title: 'Cancelled', description: 'Cancelled, voided, or refunded' },
};


const CHANNEL_LABEL_KEYS: Record<string, string> = {
  'dine-in': 'labels.channel.dine-in',
  'takeaway': 'labels.channel.takeaway',
  'delivery': 'labels.channel.delivery',
};

const PAYMENT_LABEL_KEYS: Record<string, string> = {
  cash: 'labels.payment.cash',
  card: 'labels.payment.card',
  unknown: 'labels.payment.unknown',
};

const findColumnByStatus = (status: OrderStatus): KitchenColumnConfig | undefined =>
  COLUMN_CONFIG.find((column) => column.statuses.includes(status));

const resolveServedStatus = (order: Pick<KitchenOrder, 'type'>): OrderStatus => {
  const type = (order.type ?? '').toLowerCase();
  return type === 'takeaway' || type === 'delivery' ? 'PICKED_UP' : 'SERVED';
};

const getDropStatusForColumn = (order: KitchenOrder, columnId: KitchenColumnId): OrderStatus | null => {
  switch (columnId) {
    case 'NEW_ORDER':
      return order.status === 'PENDING_PAYMENT' ? 'PENDING_PAYMENT' : 'PAID';
    case 'ACCEPTED':
      return 'ACCEPTED';
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'COMPLETED':
      return 'READY';
    case 'SERVED':
      return resolveServedStatus(order);
    case 'CANCELLED':
      if (order.status === 'VOIDED' || order.status === 'REFUNDED') {
        return order.status;
      }
      return 'CANCELLED';
    default:
      return null;
  }
};

const getAdvanceStatusForColumn = (order: KitchenOrder, columnId: KitchenColumnId): OrderStatus | null => {
  switch (columnId) {
    case 'NEW_ORDER':
      return 'ACCEPTED';
    case 'ACCEPTED':
      return 'IN_PROGRESS';
    case 'IN_PROGRESS':
      return 'READY';
    case 'COMPLETED':
      return order.status === 'READY' ? resolveServedStatus(order) : null;
    case 'SERVED':
      return order.status === 'SERVED' || order.status === 'PICKED_UP' ? 'COMPLETED' : null;
    case 'CANCELLED':
      return null;
    default:
      return null;
  }
};

const useNow = (interval = 15000) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), interval);
    return () => window.clearInterval(id);
  }, [interval]);

  return now;
};

const decorateOrder = (order: KitchenOrder, now: number): DecoratedKitchenOrder => {
  const placedAtDate = order.placedAt ? new Date(order.placedAt) : null;
  const elapsedSeconds = placedAtDate ? Math.max(0, Math.floor((now - placedAtDate.getTime()) / 1000)) : order.kds.elapsedSeconds ?? 0;
  const prepSeconds =
    typeof order.estimatedPrepSeconds === 'number' && Number.isFinite(order.estimatedPrepSeconds)
      ? order.estimatedPrepSeconds
      : order.kds.prepSeconds ?? null;
  const estimatedReadyAt =
    order.estimatedReadyAt ??
    order.kds.estimatedReadyAt ??
    (prepSeconds && placedAtDate ? new Date(placedAtDate.getTime() + prepSeconds * 1000).toISOString() : null);
  const remainingSeconds =
    typeof order.etaSeconds === 'number' && Number.isFinite(order.etaSeconds)
      ? Math.max(0, Math.round(order.etaSeconds))
      : prepSeconds
      ? Math.max(0, prepSeconds - elapsedSeconds)
      : null;
  const progressPercent =
    prepSeconds && prepSeconds > 0
      ? Math.min(100, Math.round(((prepSeconds - (remainingSeconds ?? prepSeconds)) / prepSeconds) * 100))
      : null;
  const overdue = Boolean(prepSeconds && remainingSeconds === 0 && elapsedSeconds > prepSeconds);

  const tableLabel =
    typeof order.tableNumber === 'number'
      ? `Table ${order.tableNumber}`
      : order.table ?? null;

  const decoratedItems = (order.items ?? []).map((item) => decorateKitchenItem(item, now));

  return {
    ...order,
    items: decoratedItems,
    table: tableLabel,
    derived: {
      placedAt: placedAtDate,
      elapsedSeconds,
      prepSeconds,
      progressPercent,
      remainingSeconds,
      estimatedReadyAt,
      overdue,
    },
  };
};

const formatDurationValue = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
};

const formatAbsoluteTimeValue = (iso?: string | null, locale?: string, timeZone?: string): string => {
  if (!iso) {
    return '\u2014';
  }
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return '\u2014';
    }
    return new Intl.DateTimeFormat(locale ?? 'en', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone ?? 'UTC',
    }).format(date);
  } catch {
    return '\u2014';
  }
};

const decorateKitchenItem = (item: KitchenOrderItem, now: number): DecoratedKitchenItem => {
  const status = (item.status ?? 'QUEUED') as KitchenOrderItemStatus;
  const startedAt = item.timerStartedAt ? Date.parse(item.timerStartedAt) : Number.NaN;
  const pausedAt = item.timerPausedAt ? Date.parse(item.timerPausedAt) : Number.NaN;
  const totalPauseMs =
    typeof item.timerTotalPauseMs === 'number' && Number.isFinite(item.timerTotalPauseMs)
      ? Math.max(0, item.timerTotalPauseMs)
      : 0;

  let elapsedMs: number | null = null;
  if (Number.isFinite(startedAt)) {
    const effectiveNow = Number.isFinite(pausedAt) ? pausedAt : now;
    elapsedMs = Math.max(0, effectiveNow - startedAt - totalPauseMs);
  }

  const targetMs =
    typeof item.prepSecondsTarget === 'number' && Number.isFinite(item.prepSecondsTarget)
      ? Math.max(0, Math.round(item.prepSecondsTarget * 1000))
      : null;

  const elapsedSeconds = elapsedMs !== null ? Math.max(0, Math.round(elapsedMs / 1000)) : null;
  let remainingSeconds: number | null = null;
  let progressPercent: number | null = null;
  let overdue = false;

  if (targetMs !== null && elapsedMs !== null) {
    const remainingMs = Math.max(0, targetMs - elapsedMs);
    remainingSeconds = Math.max(0, Math.round(remainingMs / 1000));
    progressPercent = targetMs > 0 ? Math.min(100, Math.round((elapsedMs / targetMs) * 100)) : null;
    overdue = elapsedMs > targetMs && status !== 'READY' && status !== 'CANCELLED';
  }

  const isPaused = Number.isFinite(pausedAt);
  const isRunning = Number.isFinite(startedAt) && !isPaused && status !== 'READY' && status !== 'CANCELLED';

  return {
    ...item,
    status,
    computed: {
      elapsedSeconds,
      remainingSeconds,
      progressPercent,
      overdue,
      isRunning,
      isPaused,
    },
  };
};

const KitchenPageV3 = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('kitchen');
  const { language } = useLocaleFormatter();
  const { settings: restaurantSettings } = useRestaurantSettings();
  const kitchenTimezone = restaurantSettings?.timezone ?? 'UTC';
  const { activeRestaurantId } = useTenantStore();
  const now = useNow();

  const [pendingAdvanceId, setPendingAdvanceId] = useState<string | null>(null);
  const [pendingPriorityId, setPendingPriorityId] = useState<string | null>(null);
  const [pendingPauseId, setPendingPauseId] = useState<string | null>(null);
  const [pendingNotifyId, setPendingNotifyId] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [activeDragMeta, setActiveDragMeta] = useState<{ order: DecoratedKitchenOrder; column: KitchenColumnWithCopy } | null>(null);
  const [overColumnId, setOverColumnId] = useState<KitchenColumnId | null>(null);

  const kitchenQuery = useQuery({
    queryKey: ['kds', 'orders', { restaurantId: activeRestaurantId }],
    queryFn: ({ signal }) => fetchKitchenOrders({ status: 'ALL' }, signal),
    enabled: Boolean(activeRestaurantId),
    refetchInterval: 20_000,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const decoratedOrders = useMemo(() => {
    return (kitchenQuery.data?.orders ?? []).map((order) => decorateOrder(order, now));
  }, [kitchenQuery.data?.orders, now]);

  const latestDataAt = kitchenQuery.dataUpdatedAt ?? 0;
  const dataStale = latestDataAt > 0 ? now - latestDataAt > 60_000 : false;
  const lastUpdatedIso = latestDataAt > 0 ? new Date(latestDataAt).toISOString() : null;
  const usingStaleCache = kitchenQuery.isError || dataStale;

  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map((column) => ({
        ...column,
        title: t(`columns.${column.id}.title`, { defaultValue: COLUMN_DEFAULT_COPY[column.id].title }),
        description: t(`columns.${column.id}.description`, { defaultValue: COLUMN_DEFAULT_COPY[column.id].description }),
      })),
    [t],
  );

  const groupedOrders = useMemo(() => {
    const map = new Map<KitchenColumnId, DecoratedKitchenOrder[]>();
    for (const column of COLUMN_CONFIG) {
      map.set(column.id, []);
    }
    for (const order of decoratedOrders) {
      const column = findColumnByStatus(order.status);
      if (!column) {
        continue;
      }
      map.get(column.id)?.push(order);
    }
    return map;
  }, [decoratedOrders]);

  const metrics = useMemo(() => {
    const active = decoratedOrders.filter((order) => ORDER_IN_FLIGHT_STATUSES.includes(order.status)).length;
    const urgent = decoratedOrders.filter((order) => order.kds.priority === 'URGENT').length;
    const paused = decoratedOrders.filter((order) => order.kds.isPaused).length;
    const ready = decoratedOrders.filter((order) => order.status === 'READY').length;
    return { active, urgent, paused, ready };
  }, [decoratedOrders]);

  const dropHint = useMemo(
    () =>
      t('sections.board.dropHint', {
        defaultValue: 'Drag orders between stages to update their status.',
      }),
    [t],
  );

  const formatRelativeTime = useCallback(
    (iso?: string | null) => {
      if (!iso) {
        return t('relativeTime.justNow');
      }
      const timestamp = Date.parse(iso);
      if (Number.isNaN(timestamp)) {
        return t('relativeTime.justNow');
      }
      const diffSeconds = Math.max(0, Math.floor((now - timestamp) / 1000));
      if (diffSeconds < 60) {
        return t('relativeTime.seconds_other', { count: diffSeconds });
      }
      const diffMinutes = Math.floor(diffSeconds / 60);
      if (diffMinutes < 60) {
        return t('relativeTime.minutes_other', { count: diffMinutes });
      }
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours < 24) {
        return t('relativeTime.hours_other', { count: diffHours });
      }
      const diffDays = Math.floor(diffHours / 24);
      return t('relativeTime.days_other', { count: diffDays });
    },
    [now, t],
  );

  const formatDuration = useCallback((seconds: number) => formatDurationValue(seconds), []);
  const formatAbsoluteTime = useCallback(
    (iso?: string | null) => formatAbsoluteTimeValue(iso, language, kitchenTimezone),
    [kitchenTimezone, language],
  );

  const getAdvanceAction = useCallback(
    (order: DecoratedKitchenOrder, column: KitchenColumnWithCopy) => {
      const targetStatus = getAdvanceStatusForColumn(order, column.id);
      if (!targetStatus) {
        return { targetStatus: null, advanceLabel: undefined };
      }
      const advanceLabel = t(`columns.${column.id}.action`, {
        defaultValue: t('actions.advance', { status: formatStatusName(targetStatus) }),
      });
      return { targetStatus, advanceLabel };
    },
    [t],
  );

  const transitionMutation = useMutation({
    mutationFn: async ({ orderId, status }: { orderId: string; status: OrderStatus }) => {
      setPendingAdvanceId(orderId);
      await updateKitchenOrderStatus(orderId, { status });
    },
    onSuccess: () => {
      toast({
        title: t('toasts.transitionSuccess.title'),
        description: t('toasts.transitionSuccess.description'),
      });
      queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['orders', 'recent'] });
      queryClient.invalidateQueries({ queryKey: ['kpis', 'today'] });
    },
    onError: (error) => {
      const parsed = parseApiError(error);

      if (parsed?.code === 'INVALID_TRANSITION' && parsed?.reason === 'NON_MONOTONIC') {
        toast({
          title: t('toasts.transitionInvalid.title', {
            defaultValue: 'Orders can only move between active kitchen stages',
          }),
          description: t('toasts.transitionInvalid.description', {
            defaultValue: 'Drop into a valid column or use the ticket actions to adjust.',
          }),
        });
        return;
      }

      toast({
        variant: 'destructive',
        title: t('toasts.transitionError.title'),
        description:
          error instanceof Error && !parsed && error.message
            ? error.message
            : t('toasts.transitionError.description'),
      });
    },
    onSettled: () => {
      setPendingAdvanceId(null);
    },
  });

  const priorityMutation = useMutation({
    mutationFn: async ({ orderId, priority }: { orderId: string; priority: KitchenOrderPriority }) => {
      setPendingPriorityId(orderId);
      return updateKitchenOrderPriority(orderId, { priority });
    },
    onSuccess: (response, variables) => {
      toast({
        title: t('toasts.priorityUpdated.title'),
        description: t('toasts.priorityUpdated.description', {
          priority: t(PRIORITY_CONFIG[variables.priority].labelKey),
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('toasts.priorityUpdated.error.title'),
        description: error instanceof Error ? error.message : t('toasts.priorityUpdated.error.description'),
      });
    },
    onSettled: () => {
      setPendingPriorityId(null);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: async ({ orderId, isPaused }: { orderId: string; isPaused: boolean }) => {
      setPendingPauseId(orderId);
      return updateKitchenOrderPause(orderId, { isPaused });
    },
    onSuccess: (response, variables) => {
      toast({
        title: variables.isPaused ? t('toasts.pause.paused.title') : t('toasts.pause.resumed.title'),
        description: variables.isPaused
          ? t('toasts.pause.paused.description')
          : t('toasts.pause.resumed.description'),
      });
      queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('toasts.pause.error.title'),
        description: error instanceof Error ? error.message : t('toasts.pause.error.description'),
      });
    },
    onSettled: () => {
      setPendingPauseId(null);
    },
  });

  const notifyMutation = useMutation({
    mutationFn: async ({ orderId }: { orderId: string }) => {
      setPendingNotifyId(orderId);
      return notifyKitchenOrder(orderId);
    },
    onSuccess: () => {
      toast({
        title: t('toasts.notify.title'),
        description: t('toasts.notify.description'),
      });
      queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: t('toasts.notify.error.title'),
        description: error instanceof Error ? error.message : t('toasts.notify.error.description'),
      });
    },
    onSettled: () => {
      setPendingNotifyId(null);
    },
  });

  const handleTransition = useCallback(
    (order: DecoratedKitchenOrder, status: OrderStatus | null | undefined) => {
      if (!status) {
        return;
      }
      transitionMutation.mutate({ orderId: order.id, status });
    },
    [transitionMutation],
  );

  const handlePriorityChange = useCallback(
    (order: DecoratedKitchenOrder, priority: KitchenOrderPriority) => {
      if (order.kds.priority === priority) {
        return;
      }
      priorityMutation.mutate({ orderId: order.id, priority });
    },
    [priorityMutation],
  );

  const handlePauseToggle = useCallback(
    (order: DecoratedKitchenOrder) => {
      pauseMutation.mutate({ orderId: order.id, isPaused: !order.kds.isPaused });
    },
    [pauseMutation],
  );

  const handleNotify = useCallback(
    (order: DecoratedKitchenOrder) => {
      notifyMutation.mutate({ orderId: order.id });
    },
    [notifyMutation],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DraggedOrderData | undefined;
    if (!data?.order || !data.column) {
      return;
    }
    setActiveDragMeta({ order: data.order, column: data.column });
    setOverColumnId(data.columnId);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const activeData = event.active.data.current as DraggedOrderData | undefined;
    const overId =
      (event.over?.data?.current as { columnId?: KitchenColumnId } | undefined)?.columnId ?? null;
    if (!activeData?.order || !overId) {
      setOverColumnId(null);
      return;
    }
    const targetStatus = getDropStatusForColumn(activeData.order, overId);
    if (
      targetStatus &&
      targetStatus !== activeData.order.status &&
      canTransitionStatus(activeData.order.status, targetStatus)
    ) {
      setOverColumnId(overId);
    } else {
      setOverColumnId(null);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const activeData = event.active.data.current as DraggedOrderData | undefined;
      const overId =
        (event.over?.data?.current as { columnId?: KitchenColumnId } | undefined)?.columnId ?? null;

      if (activeData?.order && overId) {
        const targetStatus = getDropStatusForColumn(activeData.order, overId);
        if (
          targetStatus &&
          targetStatus !== activeData.order.status &&
          canTransitionStatus(activeData.order.status, targetStatus)
        ) {
          handleTransition(activeData.order, targetStatus);
        } else if (targetStatus) {
          toast({
            title: t('toasts.transitionInvalid.title', {
              defaultValue: 'Orders can only move between active kitchen stages',
            }),
            description: t('toasts.transitionInvalid.description', {
              defaultValue: 'Drop into a valid column or use the ticket actions to adjust.',
            }),
          });
        }
      }

      setActiveDragMeta(null);
      setOverColumnId(null);
    },
    [handleTransition, t, toast],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragMeta(null);
    setOverColumnId(null);
  }, []);

  useEffect(() => {
    if (!activeRestaurantId) {
      return;
    }

    const channel = supabase
      .channel(`kitchen-orders-${activeRestaurantId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${activeRestaurantId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['kds', 'orders'] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurantId, queryClient]);

  return (
    <div className="px-6 pt-1 pb-6">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-8">
        <header className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
              <ChefHat className="h-8 w-8 text-primary" />
              {t('metadata.title')}
            </h1>
            <p className="text-muted-foreground">{t('metadata.description')}</p>
          </div>
        </header>

        {kitchenQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>{t('errors.load')}</AlertTitle>
            <AlertDescription>
              {(kitchenQuery.error as Error | undefined)?.message ?? t('errors.load')}
            </AlertDescription>
          </Alert>
        ) : null}

        {(isOffline || usingStaleCache) && (
          <Alert
            variant="default"
            className={cn(
              'border border-amber-200/70 bg-amber-50/70 dark:border-amber-800/50 dark:bg-amber-900/30',
              !isOffline && 'border-slate-200/70 bg-slate-50/70 dark:border-slate-800/40 dark:bg-slate-900/30',
            )}
          >
            <AlertTitle>
              {isOffline ? t('offline.banner.title', { defaultValue: 'Working offline' }) : t('offline.banner.degradedTitle', { defaultValue: 'Kitchen data may be stale' })}
            </AlertTitle>
            <AlertDescription className="space-y-1 text-sm">
              <p>
                {isOffline
                  ? t('offline.banner.description', {
                      defaultValue: 'We will resync as soon as the network is available. You can keep working from the cached board.',
                    })
                  : t('offline.banner.degradedDescription', {
                      defaultValue: 'We have not received a live update recently. Data shown below is the last known snapshot.',
                    })}
              </p>
              {lastUpdatedIso ? (
                <p className="text-xs text-muted-foreground">
                  {t('offline.banner.lastSynced', {
                    defaultValue: 'Last synced {{time}}',
                    time: formatRelativeTime(lastUpdatedIso),
                  })}
                </p>
              ) : null}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon={Activity}
            title={t('metrics.active.title')}
            description={t('metrics.active.description')}
            value={metrics.active}
            tone="sky"
          />
          <MetricCard
            icon={AlertTriangle}
            title={t('metrics.urgent.title')}
            description={t('metrics.urgent.description')}
            value={metrics.urgent}
            tone="amber"
          />
          <MetricCard
            icon={Pause}
            title={t('metrics.paused.title')}
            description={t('metrics.paused.description')}
            value={metrics.paused}
            tone="slate"
          />
          <MetricCard
            icon={CheckCircle}
            title={t('metrics.ready.title')}
            description={t('metrics.ready.description')}
            value={metrics.ready}
            tone="rose"
          />
        </div>

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="grid gap-6 xl:grid-cols-3">
            {columns.map((column) => {
              const columnOrders = groupedOrders.get(column.id) ?? [];
              const Icon = column.icon;
              return (
                <Card
                  key={column.id}
                  className={cn(
                    'card-elevated flex min-h-0 flex-col border shadow-sm',
                    column.border,
                    column.gradient,
                  )}
                >
                  <CardHeader className="flex flex-row items-start justify-between gap-3 border-b border-border/50">
                    <div className="min-w-0 flex-1 space-y-1">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Icon className="h-5 w-5" />
                        {column.title}
                      </CardTitle>
                      <CardDescription>{column.description}</CardDescription>
                    </div>
                    <Badge variant="secondary" className="font-medium">
                      {columnOrders.length}
                    </Badge>
                  </CardHeader>
                  <CardContent className="flex-1 min-h-0 p-0">
                    <ScrollArea
                      type="auto"
                      className="h-full min-h-[20rem] max-h-[calc(100vh-18rem)] px-1"
                      viewportClassName="pr-3"
                    >
                      <SortableContext
                        id={column.id}
                        items={columnOrders.map((order) => order.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <KitchenColumnDroppable
                          column={column}
                          isHighlighted={overColumnId === column.id}
                          isEmpty={columnOrders.length === 0}
                          isLoading={kitchenQuery.isLoading}
                          emptyLabel={t('sections.board.empty')}
                          loadingLabel={t('sections.board.loading')}
                          dropHint={dropHint}
                        >
                          {columnOrders.map((order) => {
                            const { targetStatus, advanceLabel } = getAdvanceAction(order, column);
                            return (
                              <SortableKitchenOrderCard
                                key={order.id}
                                order={order}
                                column={column}
                                advanceLabel={advanceLabel}
                                onAdvance={
                                  targetStatus
                                    ? () => {
                                        handleTransition(order, targetStatus);
                                      }
                                    : undefined
                                }
                                onPriorityChange={(priority) => {
                                  handlePriorityChange(order, priority);
                                }}
                                onTogglePause={() => {
                                  handlePauseToggle(order);
                                }}
                                onNotify={() => {
                                  handleNotify(order);
                                }}
                                isAdvancing={pendingAdvanceId === order.id && transitionMutation.isPending}
                                isUpdatingPriority={pendingPriorityId === order.id && priorityMutation.isPending}
                                isTogglingPause={pendingPauseId === order.id && pauseMutation.isPending}
                                isNotifying={pendingNotifyId === order.id && notifyMutation.isPending}
                                formatRelativeTime={formatRelativeTime}
                                formatAbsoluteTime={formatAbsoluteTime}
                                formatDuration={formatDuration}
                              />
                            );
                          })}
                        </KitchenColumnDroppable>
                      </SortableContext>
                    </ScrollArea>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DragOverlay dropAnimation={defaultDropAnimation}>
            {activeDragMeta ? (
              <div className="pointer-events-none">
                <KitchenOrderCard
                  order={activeDragMeta.order}
                  column={activeDragMeta.column}
                  advanceLabel={getAdvanceAction(activeDragMeta.order, activeDragMeta.column).advanceLabel}
                  onPriorityChange={() => {}}
                  onTogglePause={() => {}}
                  onNotify={() => {}}
                  isAdvancing={false}
                  isUpdatingPriority={false}
                  isTogglingPause={false}
                  isNotifying={false}
                  formatRelativeTime={formatRelativeTime}
                  formatAbsoluteTime={formatAbsoluteTime}
                  formatDuration={formatDuration}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export const MetricCard = ({
  icon: Icon,
  title,
  description,
  value,
  tone,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  value: number;
  tone: 'sky' | 'amber' | 'slate' | 'rose';
}) => {
  const toneClasses: Record<typeof tone, string> = {
    sky: 'border-sky-100 bg-sky-50/70 text-sky-800 dark:border-sky-900/40 dark:bg-sky-900/20 dark:text-sky-200',
    amber: 'border-amber-100 bg-amber-50/70 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200',
    slate: 'border-slate-200 bg-slate-50/70 text-slate-800 dark:border-slate-800/40 dark:bg-slate-900/30 dark:text-slate-100',
    rose: 'border-rose-100 bg-rose-50/70 text-rose-800 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200',
  } as const;

  return (
    <Card className={cn('card-elevated overflow-hidden border', toneClasses[tone])}>
      <CardContent className="flex flex-col gap-2 p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-medium uppercase tracking-wide opacity-75">{title}</div>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-3xl font-semibold">{value}</div>
        <div className="text-sm opacity-80">{description}</div>
      </CardContent>
    </Card>
  );
};

type KitchenColumnDroppableProps = {
  column: KitchenColumnWithCopy;
  isHighlighted: boolean;
  isEmpty: boolean;
  isLoading: boolean;
  emptyLabel: string;
  loadingLabel: string;
  dropHint: string;
  children: ReactNode;
};

const KitchenColumnDroppable = ({
  column,
  isHighlighted,
  isEmpty,
  isLoading,
  emptyLabel,
  loadingLabel,
  dropHint,
  children,
}: KitchenColumnDroppableProps): ReactElement => {
  const { setNodeRef } = useDroppable({
    id: column.id,
    data: { columnId: column.id },
  });
  const showHighlight = isHighlighted;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'space-y-4 rounded-xl p-4 transition-colors duration-200',
        showHighlight ? 'bg-primary/5 ring-1 ring-primary/50' : undefined,
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {isEmpty ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-lg border border-dashed border-border/60 bg-background/60 px-4 py-16 text-center text-sm text-muted-foreground transition-colors duration-200',
            showHighlight ? 'border-primary/50 bg-primary/5 text-primary dark:text-primary-foreground' : undefined,
          )}
        >
          <p>{isLoading ? loadingLabel : emptyLabel}</p>
          <p
            className={cn(
              'mt-3 text-xs',
              showHighlight ? 'text-primary/80 dark:text-primary-foreground/75' : 'text-muted-foreground/80',
            )}
          >
            {dropHint}
          </p>
        </div>
      ) : null}
      {children}
    </div>
  );
};

const SortableKitchenOrderCard = (props: KitchenOrderCardProps): ReactElement => {
  const {
    order,
    column,
    advanceLabel,
    onAdvance,
    onPriorityChange,
    onTogglePause,
    onNotify,
    isAdvancing,
    isUpdatingPriority,
    isTogglingPause,
    isNotifying,
    formatRelativeTime,
    formatAbsoluteTime,
    formatDuration,
  } = props;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: order.id,
    data: {
      order,
      column,
      columnId: column.id,
    } satisfies DraggedOrderData,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? 'transform 200ms cubic-bezier(0.2, 0, 0.2, 1)',
  };

  if (isDragging) {
    style.zIndex = 60;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'cursor-grab touch-pan-y transition-shadow duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0',
        isDragging ? 'cursor-grabbing opacity-90 shadow-xl' : 'hover:shadow-lg',
      )}
      {...attributes}
      {...listeners}
    >
      <KitchenOrderCard
        order={order}
        column={column}
        advanceLabel={advanceLabel}
        onAdvance={onAdvance}
        onPriorityChange={onPriorityChange}
        onTogglePause={onTogglePause}
        onNotify={onNotify}
        isAdvancing={isAdvancing}
        isUpdatingPriority={isUpdatingPriority}
        isTogglingPause={isTogglingPause}
        isNotifying={isNotifying}
        formatRelativeTime={formatRelativeTime}
        formatAbsoluteTime={formatAbsoluteTime}
        formatDuration={formatDuration}
      />
    </div>
  );
};

export const KitchenOrderCard = memo(({
  order,
  column,
  advanceLabel,
  onAdvance,
  onPriorityChange,
  onTogglePause,
  onNotify,
  isAdvancing,
  isUpdatingPriority,
  isTogglingPause,
  isNotifying,
  formatRelativeTime,
  formatAbsoluteTime,
  formatDuration,
}: KitchenOrderCardProps) => {
  const { t } = useTranslation('kitchen');
  const priorityConfig = PRIORITY_CONFIG[order.kds.priority];
  const paymentKey = PAYMENT_LABEL_KEYS[order.payment?.method ?? 'unknown'] ?? PAYMENT_LABEL_KEYS.unknown;
  const channelKey = CHANNEL_LABEL_KEYS[order.type] ?? CHANNEL_LABEL_KEYS['dine-in'];
  const hasPrepGoal = typeof order.derived.prepSeconds === 'number' && order.derived.prepSeconds > 0;
  const isPaused = order.kds.isPaused;
  const hasNotified = Boolean(order.kds.waiterNotifiedAt);
  const totalItemQuantity = useMemo(
    () => order.items.reduce((acc, item) => acc + item.quantity, 0),
    [order.items],
  );
  const stationEntries = useMemo(() => {
    if (!order.stationSnapshot) {
      return [];
    }
    return Object.values(order.stationSnapshot.stations ?? {}).sort((a, b) => {
      if (b.projectedOrdersPerMinute !== a.projectedOrdersPerMinute) {
        return b.projectedOrdersPerMinute - a.projectedOrdersPerMinute;
      }
      return b.projectedItemsPerMinute - a.projectedItemsPerMinute;
    });
  }, [order.stationSnapshot]);
  const overrideActive = order.throttleOverrideApplied || order.stationSnapshot?.overrideApplied;
  const cardRingClass = order.derived.overdue
    ? 'ring-2 ring-destructive/60'
    : order.kds.priority === 'URGENT'
    ? 'ring-2 ring-rose-500/50'
    : order.kds.priority === 'HIGH'
    ? 'ring-2 ring-amber-400/50'
    : '';

  return (
    <div
      data-column={column.id}
      className={cn('space-y-4 rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm backdrop-blur-sm', cardRingClass)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="text-sm font-semibold text-foreground leading-tight break-words">
            {order.code || order.id}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{order.table ?? t('labels.counter')}</span>
            {order.customerName ? (
              <span className="inline-flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {order.customerName}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {t('labels.walkIn')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {hasNotified ? (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
              {t('chips.notified')}
            </Badge>
          ) : null}
          {isPaused ? (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-300">
              {t('chips.paused')}
            </Badge>
          ) : null}
          {order.kds.partialReady ? (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 dark:text-amber-200">
              {t('chips.partialReady', { defaultValue: 'Partial ready' })}
            </Badge>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn('h-auto gap-2 rounded-full px-3 py-1 text-xs font-medium', priorityConfig.badge)}
                disabled={isUpdatingPriority}
              >
                <span className={cn('h-2 w-2 rounded-full', priorityConfig.dot)} />
                {t(priorityConfig.labelKey)}
                <ChevronDown className="h-3 w-3 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{t('priority.label')}</DropdownMenuLabel>
              {(Object.keys(PRIORITY_CONFIG) as KitchenOrderPriority[]).map((priority) => (
                <DropdownMenuItem
                  key={priority}
                  onClick={() => onPriorityChange(priority)}
                  className="flex items-center gap-2"
                  disabled={isUpdatingPriority}
                >
                  <span className={cn('h-2 w-2 rounded-full', PRIORITY_CONFIG[priority].dot)} />
                  <span>{t(PRIORITY_CONFIG[priority].labelKey)}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Clock className="h-4 w-4" />
          <span>{formatRelativeTime(order.placedAt)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Wallet className="h-4 w-4" />
          <span>{t(paymentKey)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <UtensilsCrossed className="h-4 w-4" />
          <span>{t(channelKey)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Timer className="h-4 w-4" />
          <span>
            {formatDuration(order.derived.elapsedSeconds)}
            {hasPrepGoal && order.derived.remainingSeconds !== null ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" aria-hidden="true" />
                {order.derived.overdue
                  ? t('labels.overdue', {
                      value: formatDuration(order.derived.elapsedSeconds - (order.derived.prepSeconds ?? 0)),
                    })
                  : `${t('labels.remaining')}: ${formatDuration(order.derived.remainingSeconds)}`}
              </span>
            ) : null}
          </span>
        </div>
        <div className="col-span-2 flex items-center gap-1.5">
          <CheckCircle className="h-4 w-4" />
          <span>
            {t('labels.itemsReady', {
              defaultValue: '{{ready}} of {{total}} items ready',
              ready: order.kds.readyQuantityTotal,
              total: totalItemQuantity,
            })}
          </span>
        </div>
      </div>

      {isPaused && order.kds.pauseReason ? (
        <div className="rounded-lg border border-amber-200/70 bg-amber-50/60 p-3 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-500/10 dark:text-amber-200">
          <div className="font-semibold uppercase tracking-wide text-[11px] opacity-80">
            {t('labels.pauseReason', { defaultValue: 'Pause reason' })}
          </div>
          <div className="mt-1 break-words">{order.kds.pauseReason}</div>
        </div>
      ) : null}

      <div className="space-y-3 text-sm text-muted-foreground">
          {order.items.slice(0, 4).map((item) => {
            const cleanedName = sanitizeDisplayText(item.name) || item.name;
            const cleanedNotes = item.notes ? sanitizeDisplayText(item.notes) : null;
            const status = item.status ?? 'QUEUED';
            const statusConfig = ITEM_STATUS_CONFIG[status] ?? ITEM_STATUS_CONFIG.QUEUED;
            const readyQuantity = item.readyQuantity ?? 0;
            const remainingQuantity = item.remainingQuantity ?? Math.max(0, item.quantity - readyQuantity);
            const timer = item.computed;

            const timerLabel = timer.isPaused
              ? t('items.timer.paused', {
                  defaultValue: 'Paused at {{time}}',
                  time:
                    timer.elapsedSeconds !== null
                      ? formatDuration(timer.elapsedSeconds)
                      : t('items.timer.startPending', { defaultValue: 'start pending' }),
                })
              : timer.remainingSeconds !== null && !timer.overdue
              ? t('items.timer.remaining', {
                  defaultValue: '{{time}} remaining',
                  time: formatDuration(timer.remainingSeconds),
                })
              : timer.elapsedSeconds !== null
              ? t('items.timer.elapsed', {
                  defaultValue: '{{time}} elapsed',
                  time: formatDuration(timer.elapsedSeconds),
                })
              : t('items.timer.idle', { defaultValue: 'Timer not started' });

            return (
              <div
                key={item.id ?? `${item.name}-${item.quantity}`}
                className="rounded-lg border border-border/50 bg-background/80 p-3 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 text-sm font-semibold text-foreground">
                      {item.quantity}
                      {'\u00D7'}
                    </span>
                    <div className="space-y-1">
                      <div className="text-foreground break-words">{cleanedName}</div>
                      {cleanedNotes ? (
                        <div className="text-xs text-muted-foreground break-words">{cleanedNotes}</div>
                      ) : null}
                    </div>
                  </div>
                  <Badge className={cn('flex items-center gap-1 text-[11px] font-medium', statusConfig.badge)}>
                    <span className={cn('h-1.5 w-1.5 rounded-full', statusConfig.dot)} />
                    {formatItemStatusName(status)}
                  </Badge>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    <span>
                      {t('items.quantityProgress', {
                        defaultValue: '{{ready}} ready • {{remaining}} remaining',
                        ready: readyQuantity,
                        remaining: remainingQuantity,
                      })}
                    </span>
                  </div>
                  {item.stationId ? (
                    <div className="flex items-center gap-1">
                      <ChefHat className="h-3 w-3" />
                      <span className="uppercase tracking-wide">{item.stationId}</span>
                    </div>
                  ) : null}
                </div>

                {typeof timer.progressPercent === 'number' && timer.progressPercent !== null ? (
                  <Progress
                    value={timer.progressPercent ?? undefined}
                    className={cn('mt-2 h-1.5', timer.overdue && 'bg-destructive/20 [&>div]:bg-destructive')}
                  />
                ) : null}

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <div
                    className={cn(
                      'flex items-center gap-1',
                      timer.overdue ? 'text-destructive dark:text-rose-300' : undefined,
                    )}
                  >
                    <Timer className="h-3 w-3" />
                    <span>{timerLabel}</span>
                  </div>
                  {timer.isPaused ? (
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                      {t('items.timer.pausedBadge', { defaultValue: 'Paused' })}
                    </Badge>
                  ) : timer.overdue ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase tracking-wide border-destructive/60 text-destructive"
                    >
                      {t('items.timer.overdueBadge', { defaultValue: 'SLA Breach' })}
                    </Badge>
                  ) : null}
                </div>
              </div>
            );
          })}
          {order.items.length > 4 ? (
            <div className="text-xs text-muted-foreground">
              {t('labels.additionalItems_other', { count: order.items.length - 4 })}
            </div>
          ) : null}
          {stationEntries.length > 0 && (
            <div className="space-y-2 rounded-xl border border-dashed border-muted/50 bg-muted/10 p-3">
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>{t('labels.stationLoad', { defaultValue: 'Station load' })}</span>
                {overrideActive ? (
                  <Badge variant="outline" className="border-amber-300/60 bg-amber-200/10 text-amber-700 dark:text-amber-200">
                    {t('chips.overrideApplied', { defaultValue: 'Override applied' })}
                  </Badge>
                ) : null}
              </div>
              <div className="space-y-1.5">
                {stationEntries.slice(0, 3).map((station) => {
                  const ordersPerMinute = Math.round(station.projectedOrdersPerMinute * 10) / 10;
                  const itemsPerMinute = Math.round(station.projectedItemsPerMinute * 10) / 10;
                  const limit = station.throttleIsHardStop
                    ? 0
                    : station.throttleMaxOrdersPerMinute ?? station.maxOrdersPerMinute ?? null;
                  const limitLabel =
                    limit !== null && limit >= 0
                      ? t('labels.ordersPerMinute', {
                          defaultValue: '{{orders}} / {{limit}} orders/min',
                          orders: ordersPerMinute,
                          limit,
                        })
                      : t('labels.ordersPerMinuteUnlimited', {
                          defaultValue: '{{orders}} orders/min',
                          orders: ordersPerMinute,
                        });
                  const itemsLabel = t('labels.itemsPerMinute', {
                    defaultValue: '{{items}} items/min',
                    items: itemsPerMinute,
                  });
                  const flagged =
                    station.throttleIsHardStop ||
                    (limit !== null && limit >= 0 && ordersPerMinute > limit);

                  const countdownLabel = formatCountdown(station.throttleEndsAt, now);
                  return (
                    <div
                      key={station.stationId}
                      className={cn(
                        'flex flex-col gap-1 rounded-lg border px-3 py-2 text-xs transition-colors',
                        flagged
                          ? 'border-destructive/50 bg-destructive/10 text-destructive dark:border-destructive/60'
                          : 'border-border/60 text-muted-foreground',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">
                          {station.name ?? station.code ?? t('labels.stationFallback', { defaultValue: 'Station' })}
                        </span>
                        {station.throttleIsHardStop ? (
                          <Badge variant="outline" className="border-destructive/60 bg-destructive/10 text-destructive">
                            {t('chips.hardStop', { defaultValue: 'Hard stop' })}
                          </Badge>
                        ) : null}
                        {station.overrideApplied ? (
                          <Badge variant="outline" className="border-amber-300/60 bg-amber-200/10 text-amber-700 dark:text-amber-200">
                            {t('chips.overrideApplied', { defaultValue: 'Override applied' })}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-foreground">
                        <span>{limitLabel}</span>
                        <span className="text-muted-foreground">{itemsLabel}</span>
                      </div>
                      {countdownLabel ? (
                        <div className="text-[10px] text-muted-foreground">
                          {t('kitchen:labels.throttleResets', { defaultValue: 'Resets in {{value}}', value: countdownLabel })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      {hasPrepGoal ? (
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t('labels.prepTarget')}</span>
            <span>
              {formatDuration(order.derived.prepSeconds ?? 0)}
              {order.derived.estimatedReadyAt ? ` - ${t('labels.readyBy', { time: formatAbsoluteTime(order.derived.estimatedReadyAt) })}` : ''}
            </span>
          </div>
          <Progress value={order.derived.progressPercent ?? 0} className="h-1.5" />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {onAdvance ? (
          <Button
            variant="secondary"
            size="sm"
            className="grow"
            onClick={onAdvance}
            disabled={isAdvancing}
          >
            {isAdvancing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
            {advanceLabel ?? t('actions.advance')}
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          onClick={onNotify}
          disabled={isNotifying}
          className="grow"
        >
          {isNotifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
          {t('actions.notify')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onTogglePause}
          disabled={isTogglingPause}
          className={cn('grow', isPaused ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground')}
        >
          {isTogglingPause ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : isPaused ? (
            <Play className="mr-2 h-4 w-4" />
          ) : (
            <Pause className="mr-2 h-4 w-4" />
          )}
          {isPaused ? t('actions.resume') : t('actions.pause')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {isPaused && order.kds.pausedAt ? <span>{t('labels.pausedAt', { time: formatRelativeTime(new Date(order.kds.pausedAt).toISOString()) })}</span> : null}
        {hasNotified
          ? <span>{t('labels.notified', { time: formatRelativeTime(order.kds.waiterNotifiedAt) })}</span>
          : <span>{t('labels.notifiedNever')}</span>}
      </div>
    </div>
  );
});
KitchenOrderCard.displayName = 'KitchenOrderCard';

export default KitchenPageV3;
